import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { BusinessHours, CollectionPointWithI18nKey, OpeningHours } from '../../../../types/collectionPoints';
import { getAssetUrl } from '../../../../utils/config';
import FirstNameField from '../../../fields/FirstNameField';
import LastNameField from '../../../fields/LastNameField';
import DynamicFieldArea from '../../DynamicFieldArea';
import './style.scss';

const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

type BusinessHourGroups = {
  weekDays: string[];
  openingHours: OpeningHours;
};

const getBusinessHourGroups = (businessHours: BusinessHours[]) =>
  businessHours
    .filter(({ weekDay }) => WEEKDAYS.includes(weekDay))
    .sort((a, b) => WEEKDAYS.indexOf(a.weekDay) - WEEKDAYS.indexOf(b.weekDay))
    .reduce<BusinessHourGroups[]>((businessHourGroups, eachBusinessHours) => {
      const businessHourGroupExists = businessHourGroups.some(
        businessHourGroup =>
          businessHourGroup.openingHours.from === eachBusinessHours.openingHours[0].from &&
          businessHourGroup.openingHours.until === eachBusinessHours.openingHours[0].until
      );
      if (businessHourGroupExists) {
        return businessHourGroups.map(openingTimeGroup => {
          if (
            openingTimeGroup.openingHours.from === eachBusinessHours.openingHours[0].from &&
            openingTimeGroup.openingHours.until === eachBusinessHours.openingHours[0].until
          ) {
            return {
              weekDays: [...openingTimeGroup.weekDays, eachBusinessHours.weekDay],
              openingHours: eachBusinessHours.openingHours[0],
            };
          } else {
            return openingTimeGroup;
          }
        });
      } else {
        return [
          ...businessHourGroups,
          { weekDays: [eachBusinessHours.weekDay], openingHours: eachBusinessHours.openingHours[0] },
        ];
      }
    }, []);

const getBusinessHourGroupsWithPublicHolidays = (businessHours: BusinessHours[]) => {
  const businessHourGroups = getBusinessHourGroups(businessHours);
  const publicHolidaysBusinessHours = businessHours.find(
    eachBusinessHours => eachBusinessHours.weekDay === 'publicholidays'
  );
  if (!publicHolidaysBusinessHours) {
    return businessHourGroups;
  } else {
    return [
      ...businessHourGroups,
      { weekDays: ['publicholidays'], openingHours: publicHolidaysBusinessHours.openingHours[0] },
    ];
  }
};

type LocationDetailsProps = {
  selectedLocationDetails: CollectionPointWithI18nKey;
};

const LocationDetails = (props: LocationDetailsProps) => {
  const {
    displayData: { description, address, businessHours, phone, distanceToSearchAddress },
    deliveryOption: { collectionPointType, carrierGroupKey, shippingOptionKey },
    i18nKey,
  } = props.selectedLocationDetails;
  const { formatMessage } = useIntl();
  const distanceInKm = Math.round((distanceToSearchAddress / 1000) * 100) / 100;

  const getWeekdayTexts = (weekdays: string[]) => {
    if (weekdays.length === 0) {
      return null;
    } else if (weekdays.length === 1) {
      return formatMessage({ id: `shipping.collectionPoint.location.weekdays.${weekdays[0]}` });
    }
    const initialIndex = WEEKDAYS.indexOf(weekdays[0]);
    if (weekdays.every((weekday, index) => initialIndex + index === WEEKDAYS.indexOf(weekday))) {
      return [weekdays[0], weekdays[weekdays.length - 1]]
        .map(weekday => formatMessage({ id: `shipping.collectionPoint.location.weekdays.${weekday}` }))
        .join(' - ');
    } else {
      return weekdays
        .map(weekday => formatMessage({ id: `shipping.collectionPoint.location.weekdays.${weekday}` }))
        .join(', ');
    }
  };

  return (
    <>
      <div className="location-details">
        <div className="location-details__row">
          <div className={classNames('location-details__image', 'location-details__image--carrier')}>
            <img src={getAssetUrl(`collectionPoints/mapPins/${i18nKey}.svg`)} />
          </div>
          <div className="location-details__text">
            <p className="location-details__title">
              {description || <FormattedMessage id={`shipping.collectionPoint.types.${i18nKey}`} />}
            </p>
            <div className="location-details__distance">
              <img src={getAssetUrl('collectionPoints/location.svg')} />
              <p>
                <FormattedMessage id="shipping.collectionPoint.location.distanceAway" values={{ distanceInKm }} />
              </p>
            </div>
          </div>
        </div>
        <div className="location-details__row">
          <div className={classNames('location-details__image', 'location-details__image--location')}>
            <img src={getAssetUrl('collectionPoints/mapPin.svg')} />
          </div>
          <div className="location-details__text">
            <p className="location-details__address">{`${address.streetHouseNumber}, ${address.zipCode} ${address.city}`}</p>
          </div>
        </div>
        {businessHours &&
          getBusinessHourGroupsWithPublicHolidays(businessHours).map(({ weekDays, openingHours }, index) => (
            <div className="location-details__row" key={index}>
              <div className={classNames('location-details__image', { 'location-details__image--hours': !index })}>
                {!index && <img src={getAssetUrl('collectionPoints/clock.svg')} />}
              </div>
              <div className="location-details__text">
                <p className="location-details__hours-label">{getWeekdayTexts(weekDays)}</p>
                <p className="location-details__title">
                  {openingHours.from}&nbsp;-&nbsp;
                  {openingHours.until}
                </p>
              </div>
            </div>
          ))}
        {phone && (
          <div className="location-details__row">
            <div className={classNames('location-details__image', 'location-details__image--phone')}>
              <img src={getAssetUrl('collectionPoints/phone.svg')} />
            </div>
            <div className="location-details__text">
              <p className="location-details__address">{phone}</p>
            </div>
          </div>
        )}
        <p className="location-details__fields-headline">
          <FormattedMessage id="shipping.collectionPoint.location.completeData" />
        </p>
        <div className="location-details__row location-details__row--names">
          <div className="location-details__field-container">
            <FirstNameField testIdPrefix="shippingAddress" context="shippingAddress" />
          </div>
          <div className="location-details__field-container">
            <LastNameField testIdPrefix="shippingAddress" context="shippingAddress" />
          </div>
        </div>
        <DynamicFieldArea
          area="collectionPoint"
          context="collectionPoint.additionalFields"
          conditionValues={{ collectionPointType, carrierGroupKey, shippingOptionKey }}
        />
      </div>
    </>
  );
};

export default LocationDetails;
